import { IS_SHELL } from './index';
import { routeForShellUrl } from './deeplink';

/**
 * Everything that talks to the native side. Each function is a no-op outside shell mode, and each Capacitor
 * package is behind a dynamic `import()` inside an `if (IS_SHELL)` — so in the web build rollup removes the
 * branch, the import with it, and no Capacitor code is ever downloaded by a browser.
 */

/**
 * Opens a URL in the SYSTEM browser rather than this webview. Used for social sign-in, where the point is that
 * the customer sees the provider's own address bar, and for the "manage your subscription on the web" link.
 *
 * Falls back to a plain navigation if the plugin is unavailable for any reason: leaving the app is a worse
 * outcome than the flow simply not starting, but not starting at all is the worst of the three.
 */
export async function openExternal(url: string): Promise<void> {
  if (!IS_SHELL) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
  } catch {
    window.location.assign(url);
  }
}

/**
 * Dismisses whatever `openExternal` presented, if anything still is.
 *
 * On iOS `Browser.open` is a modally presented SFSafariViewController: when the provider's final redirect
 * brings the app forward, the app navigates *underneath* a sheet still showing the provider's last page,
 * and the customer has to dismiss it by hand. Android's Custom Tab closes itself, but the plugin
 * implements `close()` there too, so one call covers both. iOS rejects when nothing is presented — that
 * is the normal case on the web-launched path, not an error.
 */
export async function closeExternal(): Promise<void> {
  if (!IS_SHELL) return;
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.close();
  } catch {
    /* nothing was presented */
  }
}

/**
 * Listens for `eu.solmiro.app://…` and hands the route to the caller (main.tsx passes a function that pushes it
 * onto the history the router is reading).
 *
 * Two entry points, because the OS has two: an app that was already running gets `appUrlOpen`, and an app that
 * the link had to launch gets the URL from `getLaunchUrl` on the way up. Missing the second is the bug where
 * signing in works only on the second attempt.
 */
export async function watchShellUrls(navigate: (route: string) => void): Promise<void> {
  if (!IS_SHELL) return;
  try {
    const { App } = await import('@capacitor/app');
    await App.addListener('appUrlOpen', ({ url }) => {
      const route = routeForShellUrl(url);
      // '/' is what a non-allowlisted path resolves to; the launch branch below already ignores it, and
      // a stranger's deep link must not be able to yank a running app off the page it is on.
      if (!route || route === '/') return;
      // Fire and forget: the navigation must not wait on the sheet's dismissal animation.
      void closeExternal();
      navigate(route);
    });
    // The app may have been started BY the link; `appUrlOpen` does not fire for that one.
    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      const route = routeForShellUrl(launch.url);
      if (route && route !== '/') navigate(route);
    }
  } catch (error: unknown) {
    // A shell that cannot hear deep links can still be signed into with an e-mail address and a password.
    console.warn('[shell] deep-link listener unavailable', error);
  }
}

/** What the native push registration produced: a device token, and which rail it belongs to. */
export interface NativeRegistration {
  token: string;
  platform: 'apns' | 'fcm';
}

/**
 * Asks the OS for permission and registers with APNs/FCM, resolving with the device token.
 *
 * The token is not a URL and carries no encryption keys — a native push is encrypted by the transport, not by
 * us — so it is posted to the same `POST /me/push-subscriptions` the browser uses, with `platform` saying which
 * rail must carry it. That is the whole of the client's half of phase 3.
 *
 * Resolves null when the customer says no, or when the registration never arrives: neither is an error, and
 * neither should be reported as one.
 */
export async function registerNativePush(timeoutMs = 15_000): Promise<NativeRegistration | null> {
  if (!IS_SHELL) return null;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const { Capacitor } = await import('@capacitor/core');

  let status = await PushNotifications.checkPermissions();
  if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
    status = await PushNotifications.requestPermissions();
  }
  if (status.receive !== 'granted') return null;

  const platform: 'apns' | 'fcm' = Capacitor.getPlatform() === 'ios' ? 'apns' : 'fcm';

  // `register()` is fire-and-forget; the token comes back on an event, and on a device with no network it
  // never comes back at all. A promise that can only settle by timing out would leave the button spinning.
  return await new Promise<NativeRegistration | null>((resolve) => {
    let settled = false;
    const finish = (value: NativeRegistration | null) => {
      if (settled) return;
      settled = true;
      void PushNotifications.removeAllListeners();
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);

    void PushNotifications.addListener('registration', ({ value }) => {
      clearTimeout(timer);
      finish(value ? { token: value, platform } : null);
    });
    void PushNotifications.addListener('registrationError', () => {
      clearTimeout(timer);
      finish(null);
    });
    void PushNotifications.register();
  });
}

/** Undoes the registration on this device. Best effort: the row is pruned by the first "gone" the rail gets. */
export async function unregisterNativePush(): Promise<void> {
  if (!IS_SHELL) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.unregister();
    await PushNotifications.removeAllListeners();
  } catch {
    /* nothing here is worth an error message on screen */
  }
}

/**
 * Whether this device already holds a granted push permission — the shell's answer to the question the web
 * build asks the Notification API.
 */
export async function nativePushPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!IS_SHELL) return 'denied';
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const status = await PushNotifications.checkPermissions();
    return status.receive === 'granted' ? 'granted' : status.receive === 'denied' ? 'denied' : 'prompt';
  } catch {
    return 'denied';
  }
}

/**
 * Routes a tapped notification, in the shell, the way `push-sw.js` routes one in the browser: the payload's
 * `url` is an absolute portal URL, and only its path is used — the app has no other origin to go to.
 */
export async function watchNotificationTaps(navigate: (route: string) => void): Promise<void> {
  if (!IS_SHELL) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const raw = action.notification.data?.url;
      if (typeof raw !== 'string' || raw.length === 0) return;
      try {
        const parsed = new URL(raw, 'https://app.solmiro.eu');
        navigate(`${parsed.pathname}${parsed.search}`);
      } catch {
        /* a payload we cannot read is a payload we do not follow */
      }
    });
  } catch (error: unknown) {
    console.warn('[shell] notification tap listener unavailable', error);
  }
}

// ---------------------------------------------------------------------------------------------
// The native-feel contract. No-op on web, and no-op for good: a browser has no taptic engine, its
// keyboard does not resize the viewport the way a webview's does, it has no hardware back button, and
// it has no system bars to style. Every one of these is behind `IS_SHELL` and a dynamic import, so the
// web bundle still contains no Capacitor code.
//
// None of them throws. A phone that will not buzz, a keyboard whose height we never learn and a status
// bar left in the wrong style are all cosmetic; a rejected promise in a boot path is not.
// ---------------------------------------------------------------------------------------------

/**
 * The vocabulary of native feedback, the same on both platforms: three impact weights, the tick a picker
 * makes as it passes a value, and the three notification patterns.
 */
export type HapticKind = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';

/**
 * A single tap of haptic feedback.
 *
 * Never awaited for correctness: a buzz that does not happen must never delay, or fail, the thing it was
 * confirming. Hence the swallowed error — on Android the whole thing depends on a system setting the
 * customer may well have turned off, and that is not something to report.
 */
export async function haptic(kind: HapticKind): Promise<void> {
  if (!IS_SHELL) return;
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    switch (kind) {
      case 'light':
        await Haptics.impact({ style: ImpactStyle.Light });
        return;
      case 'medium':
        await Haptics.impact({ style: ImpactStyle.Medium });
        return;
      case 'heavy':
        await Haptics.impact({ style: ImpactStyle.Heavy });
        return;
      case 'selection':
        await Haptics.selectionChanged();
        return;
      case 'success':
        await Haptics.notification({ type: NotificationType.Success });
        return;
      case 'warning':
        await Haptics.notification({ type: NotificationType.Warning });
        return;
      case 'error':
        await Haptics.notification({ type: NotificationType.Error });
        return;
    }
  } catch {
    /* a phone that will not buzz is still a phone that works */
  }
}

/**
 * Reports the on-screen keyboard's state and height, so a fixed footer can step above it instead of being
 * covered by it. Resolves once the listeners are attached; `onChange` fires on every show and hide.
 *
 * Three things happen on every event, and they are three because there are three kinds of consumer:
 *
 *   `<html data-keyboard="open">`  the CSS half of the contract — `--sf-tabbar-h` collapses to 0px with it
 *                                  (styles.css), so the tab bar, `pb-chrome` and every fixed bottom element
 *                                  get out of the keyboard's way in one paint.
 *   `--sf-keyboard-h`              the height in px, for anything that has to measure rather than hide.
 *   `onChange(open, heightPx)`     the JS half, for the callers that need a callback.
 *
 * `keyboardWillShow` and `keyboardDidHide` on purpose: the show event has to land BEFORE the frame the
 * keyboard animates in on, or the tab bar is still on screen underneath it; the hide event has to land
 * AFTER, or the layout snaps back while the keyboard is still sliding down over it. Both events exist on
 * both platforms — Android's Keyboard.java emits the same four names iOS does.
 *
 * The focused field is also scrolled to the middle of what is left of the viewport, one frame later so the
 * measurement is taken after the resize. `scroll-margin-bottom` on the inputs handles the ordinary case;
 * this handles the one where the field is already above the fold but the keyboard covers it anyway.
 */
export async function watchKeyboard(onChange: (open: boolean, heightPx: number) => void): Promise<void> {
  if (!IS_SHELL) return;
  const root = document.documentElement;

  const apply = (open: boolean, heightPx: number) => {
    if (open) root.dataset.keyboard = 'open';
    else delete root.dataset.keyboard;
    root.style.setProperty('--sf-keyboard-h', `${Math.max(0, Math.round(heightPx))}px`);
    onChange(open, heightPx);
  };

  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    await Keyboard.addListener('keyboardWillShow', (info) => {
      apply(true, info.keyboardHeight);
      // One frame later: the webview has been resized by then (ios.resize = 'native'), so `block: 'center'`
      // centres the field in the space that is actually left rather than in the space that just went away.
      requestAnimationFrame(() => {
        const focused = document.activeElement;
        if (focused instanceof HTMLElement) focused.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    });
    await Keyboard.addListener('keyboardDidHide', () => apply(false, 0));
  } catch (error: unknown) {
    console.warn('[shell] keyboard listener unavailable', error);
  }
}

/**
 * Android's hardware/gesture back button. `handler` returns true when it consumed the event — a sheet it
 * closed, a step it went back through — and false to let the caller's own fallback happen.
 *
 * Registering ANY listener replaces Capacitor's default behaviour (go back if the webview can, otherwise
 * finish the activity), so whoever passes a handler here owns every case, including leaving the app. The
 * root listener in main.tsx does.
 *
 * No-op on web and on iOS: a browser has its own back button, and iOS has the edge swipe (which WKWebView
 * serves from the router's own history — see SolmiroViewController).
 */
export async function watchBackButton(handler: () => boolean): Promise<void> {
  if (!IS_SHELL) return;
  try {
    const { App } = await import('@capacitor/app');
    await App.addListener('backButton', () => {
      handler();
    });
  } catch (error: unknown) {
    console.warn('[shell] back-button listener unavailable', error);
  }
}

/**
 * Keeps the status bar's (and, on Android, the navigation bar's) glyphs legible against whatever the page
 * is currently painting behind them, and keeps doing it for as long as the app is open.
 *
 * The theme is resolved exactly the way theme.css and charts/theme.ts resolve it: an explicit
 * `data-theme` on <html> wins, and otherwise dark is the default — only an OS that asks for light gets
 * light. That last part is why `SystemBarsStyle.Default` is not good enough on its own: a customer who has
 * pinned the portal to dark on a phone set to light would get dark glyphs on a navy bar, which is a bar
 * you cannot read.
 *
 * Note the inversion in the enum's own words: `Dark` means "light system-bar CONTENT on a dark
 * background". A dark page therefore asks for `Dark`.
 *
 * Neither bar is ever hidden. Both are part of the OS, and an app that swallows them is an app whose
 * customer cannot see the time or find their way home.
 */
export async function syncSystemBars(): Promise<void> {
  if (!IS_SHELL) return;
  try {
    const { SystemBars, SystemBarsStyle, SystemBarType, Capacitor } = await import('@capacitor/core');

    const prefersDark = (): boolean => {
      const explicit = document.documentElement.dataset.theme;
      if (explicit === 'dark') return true;
      if (explicit === 'light') return false;
      return !window.matchMedia?.('(prefers-color-scheme: light)').matches;
    };

    const android = Capacitor.getPlatform() === 'android';

    const sync = () => {
      const style = prefersDark() ? SystemBarsStyle.Dark : SystemBarsStyle.Light;
      void SystemBars.setStyle({ style, bar: SystemBarType.StatusBar }).catch(() => {});
      // iOS's "NavigationBar" is the home indicator, which has no style to set; Android's is a real bar
      // with real icons on it, and it sits under the tab bar.
      if (android) void SystemBars.setStyle({ style, bar: SystemBarType.NavigationBar }).catch(() => {});
    };

    // Both ways the answer can change while the app is open: the OS flipping at sunset, and the customer
    // choosing a theme in Account.
    window.matchMedia?.('(prefers-color-scheme: light)').addEventListener('change', sync);
    new MutationObserver(sync).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    sync();
  } catch (error: unknown) {
    console.warn('[shell] system bars unavailable', error);
  }
}
